import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { itemsApi } from '../api/items';
import type { MangaItem } from '../types';

/**
 * No Cover カードが画面に入ったとき、1ページ目を自動取得してサムネイルに設定するフック。
 *
 * - Intersection Observer でカードの表示を検知
 * - getPages API で1ページ目の S3 キーと URL を取得
 * - updateThumbnail API で DynamoDB に永続保存
 * - ローカル state と React Query キャッシュを即座に更新して描画反映
 */
export function useAutoSetThumbnail(item: MangaItem) {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const hasFetched = useRef(false); // 重複呼び出し防止
  const queryClient = useQueryClient();

  useEffect(() => {
    // サムネイル設定済み、またはすでに処理中なら不要
    if (item.thumbnail_url || hasFetched.current) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        if (!entries[0].isIntersecting || hasFetched.current) return;
        hasFetched.current = true;
        observer.disconnect();

        try {
          // 1. 1ページ目を取得
          const pagesResp = await itemsApi.getPages(item.id);
          const firstPage = pagesResp.pages?.[0];
          if (!firstPage) return;

          // 2. 即座に描画更新（presigned URL を使用）
          setDisplayUrl(firstPage.url);

          // 3. DynamoDB に永続保存
          await itemsApi.updateThumbnail(item.id, firstPage.s3_key);

          // 4. React Query キャッシュ更新（同一セッション内の再レンダリング対応）
          queryClient.setQueriesData(
            { queryKey: ['items'] },
            (old: unknown) => {
              if (
                !old ||
                typeof old !== 'object' ||
                !('items' in old) ||
                !Array.isArray((old as { items: unknown }).items)
              ) {
                return old;
              }
              const typed = old as { items: MangaItem[] };
              return {
                ...typed,
                items: typed.items.map((i) =>
                  i.id === item.id
                    ? { ...i, thumbnail_url: firstPage.url, thumbnail_s3_key: firstPage.s3_key }
                    : i
                ),
              };
            }
          );
        } catch (e) {
          console.error('Auto-set thumbnail failed:', item.id, e);
          hasFetched.current = false; // 失敗時はリトライ可能にする
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [item.id, item.thumbnail_url, queryClient]);

  return { ref, displayUrl };
}
